import React, { useMemo } from "react";
import type { VisualEdge, VisualNode } from "@/types/knowledgeGraph";
import FilterPanel, { type FilterCriteria } from "./FilterPanel";
import InformationPanel from "./InformationPanel";
import StatisticsPanel, { type GraphStatistics } from "./StatisticsPanel";

interface GraphControlsPanelProps {
	statistics: GraphStatistics;
	nodes: VisualNode[];
	edges: VisualEdge[];
	selectedNodeId?: string | null;
	selectedEdgeId?: string | null;
	graphId?: string | null;
	onFilter: (filter: FilterCriteria) => void;
	onResetFilter: () => void;
	/** When true, panel is embedded in left sidebar (no border, no outer padding). */
	embedded?: boolean;
}

const GraphControlsPanel: React.FC<GraphControlsPanelProps> = ({
	statistics,
	nodes,
	edges,
	selectedNodeId,
	selectedEdgeId,
	graphId: _graphId,
	onFilter,
	onResetFilter,
	embedded = false,
}) => {
	const safeNodes = nodes ?? [];
	const safeEdges = edges ?? [];
	const [statsVisible, setStatsVisible] = React.useState(true);
	const [filterVisible, setFilterVisible] = React.useState(true);
	const [infoVisible, setInfoVisible] = React.useState(true);
	// Find selected node data
	const selectedNode = useMemo(() => {
		if (!selectedNodeId) return null;
		return safeNodes.find((n) => n.id === selectedNodeId) || null;
	}, [selectedNodeId, safeNodes]);

	// Find selected edge data
	const selectedEdge = useMemo(() => {
		if (!selectedEdgeId) return null;
		return safeEdges.find((e) => e.id === selectedEdgeId) || null;
	}, [selectedEdgeId, safeEdges]);

	return (
		<div
			className={`flex flex-col h-full overflow-y-auto text-primary ${embedded ? "w-full" : "w-80 surface-alt border-l border-strong"}`}
		>
			{!embedded && (
				<div className="p-4 border-b border-subtle surface-alt">
					<h2 className="text-base font-semibold text-primary">Controls</h2>
				</div>
			)}

			<div className="flex-1 p-4 space-y-4 surface">
				<StatisticsPanel
					statistics={statistics}
					visible={statsVisible}
					onToggle={() => setStatsVisible(!statsVisible)}
				/>

				<FilterPanel
					nodes={safeNodes}
					edges={safeEdges}
					onFilter={onFilter}
					onReset={onResetFilter}
					visible={filterVisible}
					onToggle={() => setFilterVisible(!filterVisible)}
				/>

				<InformationPanel
					selectedNode={selectedNode}
					selectedEdge={selectedEdge}
					visible={infoVisible}
					onToggle={() => setInfoVisible(!infoVisible)}
				/>
			</div>
		</div>
	);
};

export default GraphControlsPanel;
